// The Bodyweight trend chart (BodyweightTrendCard.js): the 7-day average as the
// main line, every weigh-in as a faint dot (amber when it sits off the trend),
// the stretch between the last weigh-in and today shaded as "no data". With a
// bodyweight goal the x-axis runs on to the goal date and adds the path the
// client needs (green, dashed) and where the current pace lands (blue, dotted).
// Presentation-only — `trend` is the result of buildBodyweightTrend
// (utils/nutrition-progress/bodyweightTrend.js), in kg. Only this file converts
// to lb, and it does it on the way into the chart, never in the data.
//
// Recharts only recognizes its own elements as direct children of the chart, so
// the reference lines/areas come from plain functions (render*), not from
// wrapper components. Free-form drawing that needs the axis scales (the goal
// marker and its labels) goes through <Customized>, which receives them.

import moment from "moment";
import PropTypes from "prop-types";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  ComposedChart,
  Customized,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useCenteredTooltip } from "../../../../../hooks/useCenteredTooltip";
import { convertUnit } from "../../../../../utils/converters/convert-units";
import { OFF_TREND_PCT } from "../../../../../utils/nutrition-progress";
import { ChartAnnotations } from "./BodyweightChartAnnotations";
import {
  buildKgTicks,
  buildXTicks,
  collectKgValues,
} from "./bodyweightChartAxes";
import {
  GOAL_COLOR,
  MUTED_COLOR,
  TREND_COLOR,
  WARN_COLOR,
} from "./bodyweightChartTheme";
import { TooltipShell } from "./viewAtoms";
import { CHART_THEME, fmtUtc, toDisplayWeight } from "./viewTokens";

const {
  tick: CHART_TICK,
  tickFontSize: CHART_TICK_SIZE,
  grid: CHART_GRID,
} = CHART_THEME;

const CHART_HEIGHT = 320;
// The plot area inside it (below the 36px top margin, above the x axis).
const CHART_PLOT_HEIGHT = 254;
// Height of one line of the "NO DATA" text at the bottom of the plot, the space
// around it, and what the value under the current-trend dot takes.
const GAP_LINE_HEIGHT = 14;
const GAP_LABEL_PADDING = 8;
const VALUE_LABEL_HEIGHT = 28;
// The trend line is drawn across the days between two weigh-ins that are at
// most this far apart (a weekly or fortnightly client); a longer gap breaks the
// line, as there is nothing to say about those days.
const TREND_BRIDGE_MAX_GAP_DAYS = 14;

const bw = (t, key, opts) =>
  t(`nutritionPage.progress.bodyweightTrend.${key}`, opts);
// Reuses the "Bodyweight" row label the other bodyweight chart tooltips use.
const hc = (t, key, opts) =>
  t(`nutritionPage.progress.hero.chart.${key}`, opts);

const toPlotValue = (kg, isImperial) => {
  if (kg == null) return null;
  return isImperial ? convertUnit(kg, "kg", "lb") : kg;
};

/* ---- data ---------------------------------------------------------------- */

// The line of the 7-day average: one point per weigh-in day (the trend of the
// days in between, which only repeats the last weigh-in, is not drawn), joined
// in a straight line to the next weigh-in when they are at most
// TREND_BRIDGE_MAX_GAP_DAYS apart.
const bridgeTrend = (trends) => {
  const line = [...trends];
  let prev = null;
  for (let i = 0; i < trends.length; i++) {
    if (trends[i] === null) continue;
    if (
      prev !== null &&
      i - prev > 1 &&
      i - prev <= TREND_BRIDGE_MAX_GAP_DAYS
    ) {
      for (let day = prev + 1; day < i; day++) {
        line[day] =
          trends[prev] +
          ((trends[i] - trends[prev]) * (day - prev)) / (i - prev);
      }
    }
    prev = i;
  }
  return line;
};

// The series, plus — with a goal — the two projection lines. Both start at the
// current trend on the last weigh-in and end on the goal date; the rows in
// between carry no value for them and the lines connect across.
const buildRows = (trend, isImperial) => {
  const { axis, goal, series } = trend;
  const rows = series.map((pt) => ({
    idx: pt.idx,
    utcDate: pt.utcDate,
    weight: toPlotValue(pt.weight, isImperial),
    trend: toPlotValue(pt.trend, isImperial),
    offTrend: pt.offTrend,
  }));
  const trendLine = bridgeTrend(
    rows.map((row) => (row.weight === null ? null : row.trend))
  );
  for (const [i, row] of rows.entries()) row.trendLine = trendLine[i];
  if (!goal || trend.isProjectionStale) return rows;

  const trendNow = toPlotValue(trend.currentTrend, isImperial);
  const projected = toPlotValue(goal.projectedWeight, isImperial);
  rows[axis.lastWeighInIdx] = {
    ...rows[axis.lastWeighInIdx],
    path: trendNow,
    pace: projected === null ? null : trendNow,
  };
  const end = {
    path: toPlotValue(goal.targetWeight, isImperial),
    pace: projected,
  };
  if (axis.goalIdx === axis.todayIdx) {
    rows[axis.todayIdx] = { ...rows[axis.todayIdx], ...end };
  } else {
    rows.push({
      idx: axis.goalIdx,
      utcDate: goal.utcTargetDate,
      weight: null,
      trend: null,
      offTrend: false,
      ...end,
    });
  }
  return rows;
};

/* ---- pieces -------------------------------------------------------------- */

// One dot per weigh-in. A weigh-in off the trend gets an amber dot with a ring.
const WeighInDot = ({ cx, cy, payload }) => {
  if (cx == null || cy == null || payload?.weight == null) return null;
  if (payload.offTrend) {
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={9}
          fill="none"
          stroke={WARN_COLOR}
          strokeOpacity={0.45}
          strokeWidth={1.5}
        />
        <circle cx={cx} cy={cy} r={5} fill={WARN_COLOR} />
      </g>
    );
  }
  return <circle cx={cx} cy={cy} r={4} fill={TREND_COLOR} fillOpacity={0.5} />;
};

WeighInDot.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  payload: PropTypes.object,
};

// Centered text inside a ReferenceArea, at its bottom edge. Recharts injects
// `viewBox` into a custom label element.
const AreaBottomLabel = ({ viewBox, lines, color }) => {
  if (!viewBox) return null;
  const x = viewBox.x + viewBox.width / 2;
  const lineHeight = 14;
  const y = viewBox.y + viewBox.height - 8 - (lines.length - 1) * lineHeight;
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill={color}
      fontSize={11}
      fontWeight={700}
    >
      {lines.map((line, i) => (
        <tspan key={line} x={x} dy={i === 0 ? 0 : lineHeight}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

AreaBottomLabel.propTypes = {
  viewBox: PropTypes.object,
  lines: PropTypes.arrayOf(PropTypes.string).isRequired,
  color: PropTypes.string,
};

const WeightTooltip = ({ active, payload, unit, onMeasureWidth }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <TooltipShell
      active={active}
      payload={payload}
      onMeasureWidth={onMeasureWidth}
      isEmpty={(pt) => pt?.weight == null}
    >
      {(pt) => (
        <>
          <div className="np-hero__tooltip-date">
            {moment(String(pt.utcDate), "YYYYMMDD").format("MMM D, YYYY")}
          </div>
          <div className="np-hero__tooltip-row" style={{ color: TREND_COLOR }}>
            <span>{hc(t, "bodyweight")}:</span>
            <span>
              {Math.round(pt.weight * 10) / 10} {unit}
            </span>
          </div>
        </>
      )}
    </TooltipShell>
  );
};

WeightTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  unit: PropTypes.string,
  onMeasureWidth: PropTypes.func,
};

const LegendItem = ({ item }) => (
  <span className="np-bwtrend__legend-item" style={{ color: item.color }}>
    {item.kind === "dot" ? (
      <span
        className="np-bwtrend__legend-dot"
        style={item.faint ? { opacity: 0.6 } : undefined}
      />
    ) : (
      <span
        className={
          item.dash
            ? `np-bwtrend__legend-line np-bwtrend__legend-line--${item.dash}`
            : "np-bwtrend__legend-line"
        }
      />
    )}
    <span className="np-bwtrend__legend-text">{item.label}</span>
  </span>
);

LegendItem.propTypes = { item: PropTypes.object.isRequired };

const TrendLegend = ({ items, hint }) => (
  <div className="np-bwtrend__legend">
    <div className="np-bwtrend__legend-items">
      {items.map((item) => (
        <LegendItem key={item.key} item={item} />
      ))}
    </div>
    <span className="np-bwtrend__legend-hint">{hint}</span>
  </div>
);

TrendLegend.propTypes = {
  items: PropTypes.array.isRequired,
  hint: PropTypes.string,
};

// With a goal the legend also lists the two projection lines. The amber dots
// are explained only when the range shows at least one.
const buildLegendItems = (t, { hasGoal, hasPace, hasOffTrend }) => {
  const items = [
    {
      key: "average",
      kind: "line",
      color: TREND_COLOR,
      label: bw(t, "chart.legend.average"),
    },
    {
      key: "weighIn",
      kind: "dot",
      faint: true,
      color: TREND_COLOR,
      label: bw(t, "chart.legend.weighIn"),
    },
  ];
  if (hasPace) {
    items.push({
      key: "atPace",
      kind: "line",
      dash: "dotted",
      color: TREND_COLOR,
      label: bw(t, "chart.legend.atPace"),
    });
  }
  if (hasGoal) {
    items.push({
      key: "pathToGoal",
      kind: "line",
      dash: "dashed",
      color: GOAL_COLOR,
      label: bw(t, "chart.legend.pathToGoal"),
    });
  }
  if (!hasOffTrend) return items;
  items.push({
    key: "offTrend",
    kind: "dot",
    color: WARN_COLOR,
    label: bw(t, "chart.legend.offTrend", {
      pct: Math.round(OFF_TREND_PCT * 1000) / 10,
    }),
  });
  return items;
};

/* ---- reference elements --------------------------------------------------- */

const renderGapElements = (axis, lines) => {
  if (axis.todayIdx <= axis.lastWeighInIdx) return null;
  return [
    <ReferenceArea
      key="gap-area"
      x1={axis.lastWeighInIdx}
      x2={axis.todayIdx}
      fill={WARN_COLOR}
      fillOpacity={0.12}
      stroke="none"
      ifOverflow="visible"
      label={<AreaBottomLabel lines={lines} color={WARN_COLOR} />}
    />,
    <ReferenceLine
      key="gap-line"
      x={axis.lastWeighInIdx}
      stroke={WARN_COLOR}
      strokeOpacity={0.7}
      strokeDasharray="4 4"
      ifOverflow="visible"
    />,
  ];
};

const renderProjectionArea = (axis, goal) => {
  if (!goal || axis.goalIdx <= axis.todayIdx) return null;
  return (
    <ReferenceArea
      x1={axis.todayIdx}
      x2={axis.goalIdx}
      fill={MUTED_COLOR}
      fillOpacity={0.06}
      stroke="none"
      ifOverflow="visible"
    />
  );
};

/* ---- chart --------------------------------------------------------------- */

export const BodyweightTrendChart = memo(({ trend, units }) => {
  const { t } = useTranslation("athlete-portal");
  const isImperial = units === "imperial";
  const unit = isImperial ? "lb" : "kg";
  const { axis, series, goal } = trend;
  const maxIdx = goal ? axis.goalIdx : axis.todayIdx;

  const rows = useMemo(() => buildRows(trend, isImperial), [trend, isImperial]);

  // The goal label goes above its dot when the client is heading up to it and
  // below when heading down, so that is the side that needs free room.
  const currentTrend = trend.currentTrend;
  const yTicks = useMemo(() => {
    const values = collectKgValues(series, goal);
    // Free room under the lowest value, where the "NO DATA" text and the
    // value label of the current trend sit.
    const reserves = [{ kg: Math.min(...values), side: "below" }];
    if (goal) {
      reserves.push({
        kg: goal.targetWeight,
        side: goal.targetWeight < currentTrend ? "below" : "above",
      });
    }
    return buildKgTicks(values, reserves).map((kg) =>
      toPlotValue(kg, isImperial)
    );
  }, [series, goal, currentTrend, isImperial]);

  const xTicks = useMemo(
    () => buildXTicks(maxIdx, Boolean(goal)),
    [maxIdx, goal]
  );
  const fromDate = useMemo(
    () => moment(String(axis.fromUtc), "YYYYMMDD"),
    [axis.fromUtc]
  );
  const spansYears = maxIdx >= 365;
  const tickLabel = (idx) =>
    fromDate
      .clone()
      .add(idx, "days")
      .format(spansYears ? "MMM YYYY" : "MMM D");

  const { containerRef, tooltipPosition, onMeasureWidth, chartHandlers } =
    useCenteredTooltip({ initialWidth: 160 });

  if (trend.daysLogged === 0) {
    return <div className="np-bwtrend__empty">{bw(t, "noDataInRange")}</div>;
  }

  const gapLines = [bw(t, "chart.noDataGap")];
  if (!goal) {
    gapLines.push(
      bw(t, "chart.noDataDays", { count: trend.daysSinceLastWeighIn })
    );
  }
  // With a last weigh-in too old to project from (see buildBodyweightTrend) the
  // goal marker stays but the path and pace lines do not.
  const showProjection = Boolean(goal) && !trend.isProjectionStale;
  // The value goes under the current-trend dot, unless the "NO DATA" text at the
  // bottom of the plot needs that room (a dot low in the plot): then above it.
  const trendPlot = toPlotValue(trend.currentTrend, isImperial);
  const roomBelowDot =
    ((trendPlot - yTicks[0]) / (yTicks.at(-1) - yTicks[0])) * CHART_PLOT_HEIGHT;
  const gapNeeds =
    axis.todayIdx > axis.lastWeighInIdx
      ? gapLines.length * GAP_LINE_HEIGHT +
        GAP_LABEL_PADDING +
        VALUE_LABEL_HEIGHT
      : 0;
  const valueLabelPosition = roomBelowDot < gapNeeds ? "top" : "bottom";
  // The current-trend dot sits on the last weigh-in and would hide its amber.
  const lastWeighInOffTrend = series.some(
    (pt) => pt.idx === axis.lastWeighInIdx && pt.offTrend
  );
  const projectedPlot = goal
    ? toPlotValue(goal.projectedWeight, isImperial)
    : null;
  const projectionLabels = goal && {
    goal: bw(t, "chart.goalLabel", {
      value: toDisplayWeight(goal.targetWeight, isImperial).toFixed(1),
      unit: unit.toUpperCase(),
    }),
    by: bw(t, "chart.goalBy", { date: fmtUtc(goal.utcTargetDate) }),
    pace:
      goal.projectedWeight === null
        ? null
        : bw(t, "chart.atPace", {
            value: toDisplayWeight(goal.projectedWeight, isImperial).toFixed(1),
          }),
  };

  return (
    <div className="np-bwtrend__chart" ref={containerRef}>
      <ResponsiveContainer width="99%" height={CHART_HEIGHT}>
        <ComposedChart
          data={rows}
          margin={{ top: 36, right: 16, bottom: 0, left: 0 }}
          {...chartHandlers}
        >
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="idx"
            type="number"
            domain={[0, Math.max(maxIdx, 1)]}
            ticks={xTicks}
            tickFormatter={tickLabel}
            tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
            tickLine={false}
            axisLine={false}
            padding={{ left: 8, right: 8 }}
          />
          <YAxis
            domain={[yTicks[0], yTicks.at(-1)]}
            ticks={yTicks}
            tickFormatter={(value) =>
              isImperial ? value.toFixed(1) : String(Math.round(value))
            }
            tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
            tickLine={false}
            axisLine={false}
            width={44}
          />
          {renderGapElements(axis, gapLines)}
          {renderProjectionArea(axis, goal)}
          <ReferenceLine
            x={axis.todayIdx}
            stroke={MUTED_COLOR}
            strokeWidth={1.5}
            ifOverflow="visible"
            label={{
              value: bw(t, "chart.today"),
              position: "top",
              fill: "var(--primary)",
              fontSize: 11,
              fontWeight: 700,
            }}
          />
          <Tooltip
            content={
              <WeightTooltip unit={unit} onMeasureWidth={onMeasureWidth} />
            }
            cursor={{ stroke: "var(--primary)", strokeWidth: 1 }}
            position={tooltipPosition}
          />
          {showProjection ? (
            <Line
              dataKey="path"
              stroke={GOAL_COLOR}
              strokeWidth={2}
              strokeDasharray="7 5"
              dot={false}
              activeDot={false}
              connectNulls
              isAnimationActive={false}
            />
          ) : null}
          {showProjection && projectedPlot !== null ? (
            <Line
              dataKey="pace"
              stroke={TREND_COLOR}
              strokeWidth={2}
              strokeDasharray="2 4"
              strokeLinecap="round"
              dot={false}
              activeDot={false}
              connectNulls
              isAnimationActive={false}
            />
          ) : null}
          <Line
            dataKey="trendLine"
            stroke={TREND_COLOR}
            strokeWidth={2.5}
            dot={false}
            activeDot={false}
            type="monotone"
            isAnimationActive={false}
          />
          <Line
            dataKey="weight"
            stroke="none"
            dot={<WeighInDot />}
            activeDot={{ r: 4, fill: TREND_COLOR, stroke: "none" }}
            isAnimationActive={false}
          />
          <ReferenceDot
            x={axis.lastWeighInIdx}
            y={trendPlot}
            r={4.5}
            fill={lastWeighInOffTrend ? WARN_COLOR : TREND_COLOR}
            stroke="none"
            ifOverflow="visible"
            label={{
              value: toDisplayWeight(trend.currentTrend, isImperial).toFixed(1),
              position: valueLabelPosition,
              offset: 6,
              fill: "var(--primary)",
              fontSize: 12,
              fontWeight: 700,
            }}
          />
          {goal ? (
            <Customized
              component={
                <ChartAnnotations
                  rows={rows}
                  projection={
                    goal && {
                      todayIdx: axis.todayIdx,
                      goalIdx: axis.goalIdx,
                      startValue: toPlotValue(trend.currentTrend, isImperial),
                      targetValue: toPlotValue(goal.targetWeight, isImperial),
                      projectedValue: projectedPlot,
                      labels: projectionLabels,
                      title:
                        axis.goalIdx > axis.todayIdx
                          ? bw(t, "chart.projection")
                          : null,
                    }
                  }
                />
              }
            />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
      <TrendLegend
        items={buildLegendItems(t, {
          hasGoal: showProjection,
          hasPace: showProjection && projectedPlot !== null,
          hasOffTrend: series.some((pt) => pt.offTrend),
        })}
        hint={bw(t, "chart.legend.hint")}
      />
    </div>
  );
});

BodyweightTrendChart.propTypes = {
  trend: PropTypes.object.isRequired,
  units: PropTypes.string,
};
